import React, { useEffect, useMemo, useRef } from "react";
import { withRouter, useHistory, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { RevCard, RevEmpty, RevFigure, RevField, RevSkeleton, cx } from "../../components/ds";
import { getTimeRange, getTimeFilters } from "../../helpers/timeRanges";
import { formatBalance, formatCount } from "../../helpers/functions";
import StripePacks from "../../components/account/StripePacks";
import CreditsBars, { ESQUELETO_BARRAS } from "../../components/account/CreditsBars";
import Modal from "../../components/generic/modal";

// Filas del esqueleto de Compras: lo que cabe de la tabla antes del pliegue, no el
// tamaño de la respuesta. La card nace debajo de la card de packs de Stripe
// (`<stripe-pricing-table>`, varios cientos de px), de las pestañas y de los filtros: a
// 1440x900 su cabecera queda ya por debajo del pliegue, y a 390x844 la cabecera (38,5 px)
// mas cuatro filas de 42 px son 206,5 px, el ultimo trozo de tabla que entra en la ventana
// bajo el resto de la pantalla. Medido en el banco (medidas-2.txt, seccion «pliegue»).
const FILAS_ESQUELETO_CREDITOS = 4;
// Barras del esqueleto de Consumos: una semana. El lienzo tiene alto fijo (200 px), así
// que el recuento no mueve la altura, solo el ancho de columna; 7 queda lejos del umbral
// de 14 a partir del cual el lienzo real esconde los días (`--dense`).
const BARRAS_ESQUELETO = 7;

// Por encima de este número de meses distintos, Consumos agrupa barras y detalle por mes:
// con «Últimos 30 días» o «Este mes» se sigue viendo por día.
const MESES_VISTA_DIARIA = 2;

// Por encima de un año, la autonomía deja de ser una cifra útil (con poco consumo salían
// decenas de miles de días): se muestra «Más de 1 año» sin ritmo.
const AUTONOMIA_MAX_DIAS = 365;

const TIPOS_SUMAN = ['ADD_TOTALBALANCE', 'INIT', 'SUBTRACT_USEDBALANCE'];
const TIPOS_RESTAN = ['SUBTRACT_TOTALBALANCE', 'ADD_USEDBALANCE'];

const claveDiaUtc = (segundos) => {
    const fecha = new Date(segundos * 1000);
    const dia = String(fecha.getUTCDate()).padStart(2, '0');
    const mes = String(fecha.getUTCMonth() + 1).padStart(2, '0');
    return `${dia}/${mes}/${fecha.getUTCFullYear()}`;
};

const AccountCredit = ({
    mode,
    redirect,
    user,
    events,
    purchasesData,
    getPurchasesStats,
    loadingPurchases,
    consumptionsData,
    getConsumptionsStats,
    loadingConsumptions,
    creditMovements,
    getCreditMovements,
    loadingCreditMovements,
    creditFilters,
    setCreditFilters
}) => {
    const { t, i18n } = useTranslation();
    const history = useHistory();

    const tab = mode === 'consumptions' ? 'consumptions' : 'purchases';
    // Consumos abre en 30 días: con «Todo» el backend lee el histórico entero de accesos.
    const rangeKey = creditFilters?.rangeKey || (tab === 'consumptions' ? 'last30Days' : 'all');
    const eventId = creditFilters?.eventId;

    // «Ya se pidió», uno por serie: los efectos corren DESPUÉS del pintado, así que en el
    // primer render de una pestaña el loading todavía es false y sin esta marca la card
    // pintaría su estado vacío un frame antes de que arranque la petición.
    const pedido = useRef({ purchases: false, consumptions: false });

    useEffect(() => {
        const { from, to } = getTimeRange(rangeKey);
        if (tab === 'purchases') {
            pedido.current.purchases = true;
            getPurchasesStats({ from, to });
            return;
        }
        pedido.current.consumptions = true;
        getConsumptionsStats({ from, to, eventId });
        getCreditMovements({ from, to });
        // eslint-disable-next-line
    }, [tab, rangeKey, eventId]);

    // Primera carga = no hay datos todavía (el store los inicia en `undefined` y no los
    // borra al refrescar). Refetch con datos en pantalla = el contenido se queda y la card
    // pulsa. Si la petición falla, la acción baja el loading sin tocar los datos: la
    // guarda cede y se ve el estado vacío, nunca un esqueleto eterno.
    const primeraCompras = !Array.isArray(purchasesData) && (loadingPurchases || !pedido.current.purchases);
    const refrescandoCompras = loadingPurchases && !primeraCompras;
    const primeraConsumos = !Array.isArray(consumptionsData) && (loadingConsumptions || !pedido.current.consumptions);
    const refrescandoConsumos = loadingConsumptions && !primeraConsumos;

    const decimalFormatter = useMemo(
        () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }),
        [i18n.language]
    );
    const fmt = (n) => formatCount(n, i18n.language);

    const movimientos = useMemo(() => {
        if (!Array.isArray(purchasesData)) return [];
        return purchasesData
            .filter(item => item
                && typeof item === 'object'
                && item.operationDate
                && typeof item.quantity === 'number')
            .map(item => {
                const claveTipo = `account.credit.typeStat.${item.type}`;
                const cantidad = formatCount(item.quantity, i18n.language);
                let signo = '';
                if (TIPOS_SUMAN.includes(item.type)) signo = '+';
                else if (TIPOS_RESTAN.includes(item.type)) signo = '−';
                const hayTotal = item.totalBalance !== undefined && item.totalBalance !== null;
                const hayUsado = item.usedBalance !== undefined && item.usedBalance !== null;
                return {
                    operationDate: item.operationDate,
                    type: item.type,
                    concepto: item.note ? t(item.note) : '—',
                    tipo: item.type && i18n.exists(claveTipo) ? t(claveTipo) : '—',
                    cantidad: signo + cantidad,
                    saldo: hayTotal && hayUsado ? formatCount(item.totalBalance - item.usedBalance, i18n.language) : '—'
                };
            });
    }, [purchasesData, t, i18n]);

    const diasDesc = useMemo(() => (Array.isArray(consumptionsData) ? consumptionsData : []), [consumptionsData]);

    const consumo = useMemo(() => {
        const total = diasDesc.reduce((acc, d) => acc + (d?.quantity || 0), 0);
        const conAcceso = diasDesc.filter(d => d?.quantity > 0).length;
        const punta = diasDesc.reduce(
            (acc, d) => (acc === null || (d?.quantity || 0) > (acc.quantity || 0) ? d : acc),
            null
        );
        return {
            total,
            conAcceso,
            media: conAcceso ? total / conAcceso : 0,
            punta
        };
    }, [diasDesc]);

    // Movimientos crudos por día UTC (la misma clave que agrupa el backend): saldo al cierre
    // (cada movimiento guarda el saldo DESPUÉS de operar, así que vale el más reciente) y
    // participantes nuevos por evento. Es la petición que la pestaña ya hacía para los nombres.
    const movsPorDia = useMemo(() => {
        const mapa = {};
        if (!Array.isArray(creditMovements)) return mapa;
        creditMovements.forEach(m => {
            const segundos = Number(m?.operationDate);
            if (!Number.isFinite(segundos)) return;
            const clave = claveDiaUtc(segundos);
            if (!mapa[clave]) mapa[clave] = { ultimo: -1, saldo: null, eventos: {} };
            const dia = mapa[clave];
            const hayTotal = m.totalBalance !== undefined && m.totalBalance !== null;
            const hayUsado = m.usedBalance !== undefined && m.usedBalance !== null;
            if (hayTotal && hayUsado && segundos > dia.ultimo) {
                dia.ultimo = segundos;
                dia.saldo = m.totalBalance - m.usedBalance;
            }
            if (m.type === 'CODE_ACCESS' && m.eventName) {
                dia.eventos[m.eventName] = (dia.eventos[m.eventName] || 0) + (m.quantity || 0);
            }
        });
        return mapa;
    }, [creditMovements]);

    const nombreEventoFiltrado = eventId ? events?.find(e => e?.id === eventId)?.name : null;

    // Una entrada por día con su desglose por evento. El desglose solo se da si cuadra con el
    // total del día: /account/credit/stats devuelve una sola página (1 MB) y en históricos
    // grandes se queda corto; entonces se cae a la fila agregada, nunca a cifras incompletas.
    const detalleDias = useMemo(() => diasDesc
        .filter(d => d?.operationDate)
        .map(d => {
            const info = loadingCreditMovements ? null : movsPorDia[d.operationDate];
            const quantity = d.quantity || 0;
            let eventos = null;
            if (eventId) {
                if (nombreEventoFiltrado) eventos = [[nombreEventoFiltrado, quantity]];
            } else if (info) {
                const lista = Object.entries(info.eventos).sort((a, b) => b[1] - a[1]);
                if (lista.reduce((acc, [, n]) => acc + n, 0) === quantity) eventos = lista;
            }
            return {
                clave: d.operationDate,
                quantity,
                saldo: info ? info.saldo : null,
                eventos,
                nombres: info ? Object.keys(info.eventos) : []
            };
        }), [diasDesc, movsPorDia, eventId, nombreEventoFiltrado, loadingCreditMovements]);

    // Meses en orden descendente, como los días (dd/MM/yyyy → clave MM/yyyy). Saldo del mes =
    // el del día más reciente que lo tenga; desglose solo si todos sus días lo tienen.
    const meses = useMemo(() => {
        const grupos = new Map();
        detalleDias.forEach(d => {
            const clave = d.clave.slice(3);
            if (!/^\d{2}\/\d{4}$/.test(clave)) return;
            if (!grupos.has(clave)) {
                grupos.set(clave, { clave, quantity: 0, saldo: null, porEvento: {}, completo: true, nombres: new Set() });
            }
            const grupo = grupos.get(clave);
            grupo.quantity += d.quantity;
            if (grupo.saldo === null && d.saldo !== null) grupo.saldo = d.saldo;
            if (!d.eventos) grupo.completo = false;
            else d.eventos.forEach(([nombre, n]) => { grupo.porEvento[nombre] = (grupo.porEvento[nombre] || 0) + n; });
            d.nombres.forEach(n => grupo.nombres.add(n));
        });
        return Array.from(grupos.values()).map(g => ({
            clave: g.clave,
            quantity: g.quantity,
            saldo: g.saldo,
            eventos: g.completo ? Object.entries(g.porEvento).sort((a, b) => b[1] - a[1]) : null,
            nombres: Array.from(g.nombres)
        }));
    }, [detalleDias]);

    const porMes = meses.length > MESES_VISTA_DIARIA;

    const nombreMes = (clave, opciones) => {
        const [mes, anio] = clave.split('/');
        return new Intl.DateTimeFormat(i18n.language, { ...opciones, timeZone: 'UTC' })
            .format(new Date(Date.UTC(Number(anio), Number(mes) - 1, 1)));
    };

    const barras = useMemo(() => {
        const serie = porMes
            ? meses.map(m => ({
                label: nombreMes(m.clave, { month: 'short', year: '2-digit' }),
                value: m.quantity
            }))
            : diasDesc.map(d => ({
                label: (d?.operationDate || '').slice(0, 5),
                value: d?.quantity || 0
            }));
        const maximo = serie.reduce((acc, b) => (b.value > acc ? b.value : acc), 0);
        return serie
            .reverse()
            .map(b => ({ ...b, isMax: maximo > 0 && b.value === maximo }));
        // eslint-disable-next-line
    }, [porMes, meses, diasDesc, i18n.language]);

    // Autonomía: saldo actual entre el ritmo del periodo (participantes nuevos por día natural,
    // contando los días sin accesos). Sin sentido filtrando un evento: el saldo es de la cuenta.
    const autonomia = useMemo(() => {
        if (eventId || !consumo.total) return null;
        const saldo = Number(user?.balance);
        if (!Number.isFinite(saldo)) return null;
        const { from, to } = getTimeRange(rangeKey);
        const ahora = Date.now() / 1000;
        let inicio = from;
        if (inicio === undefined) {
            const masAntiguo = diasDesc[diasDesc.length - 1]?.operationDate || '';
            const [dd, mm, yyyy] = masAntiguo.split('/').map(Number);
            if (!yyyy) return null;
            inicio = Date.UTC(yyyy, mm - 1, dd) / 1000;
        }
        const diasNaturales = Math.max(1, Math.ceil(((to ?? ahora) - inicio) / 86400));
        const ritmo = consumo.total / diasNaturales;
        return { ritmo, dias: Math.max(0, Math.floor(saldo / ritmo)) };
    }, [eventId, consumo.total, user, rangeKey, diasDesc]);

    // Filas de la tabla de detalle (día o mes): una por evento si hay desglose; la fecha y el
    // saldo solo en la primera fila del grupo, para que el bloque se lea como uno.
    const filasDetalle = (grupos, etiqueta, claseFila) => grupos.flatMap(g => {
        const saldo = g.saldo === null ? '—' : fmt(g.saldo);
        const filas = g.eventos && g.eventos.length
            ? g.eventos
            : [[g.nombres.length ? g.nombres.join(' · ') : '—', g.quantity]];
        return filas.map(([nombre, n], idx) => (
            <div
                className={cx('rev-table-row', claseFila, idx < filas.length - 1 && 'rev-cred-row--sigue')}
                key={`${g.clave}-${nombre}-${idx}`}>
                <span className="rev-num">{idx === 0 ? etiqueta(g.clave) : ''}</span>
                <span className="rev-table-row__ellipsis" title={nombre}>{nombre}</span>
                <span className="rev-num rev-cred-cell--right">{n ? fmt(n) : '—'}</span>
                <span className="rev-num rev-cred-cell--right">{idx === 0 ? saldo : ''}</span>
            </div>
        ));
    });

    const eventosSeleccionables = events?.filter(event => event && event.id && event.name) || [];

    return (<>
        <div className="rev-cred-page">
            <div className="rev-cred-head">
                <h2 className="rev-page-title">{t('account.credit.title')}</h2>
                <p className="rev-help">{t('account.credit.subtitle')}</p>
            </div>

            <RevCard title={t('account.credit.purchases')}>
                <StripePacks clientReferenceId={user?.id} />
            </RevCard>

            <div className="rev-tabs" role="tablist">
                <Link
                    to="/account/credit/home"
                    className={`rev-tabs__tab ${tab === 'purchases' ? 'is-active' : ''}`}
                    aria-current={tab === 'purchases' ? 'page' : undefined}>
                    {t('account.credit.tabs.purchases')}
                </Link>
                <Link
                    to="/account/consumptions/home"
                    className={`rev-tabs__tab ${tab === 'consumptions' ? 'is-active' : ''}`}
                    aria-current={tab === 'consumptions' ? 'page' : undefined}>
                    {t('account.credit.tabs.consumptions')}
                </Link>
            </div>

            <div className="rev-cred-filters">
                <RevField label={t('account.credit.range')} htmlFor="ds-cred-range" className="rev-field--w200">
                    <select
                        id="ds-cred-range"
                        className="rev-input"
                        value={rangeKey}
                        onChange={(e) => setCreditFilters({ rangeKey: e.target.value })}>
                        {getTimeFilters(t).map(filtro => (
                            <option key={filtro.key} value={filtro.key}>{filtro.label}</option>
                        ))}
                    </select>
                </RevField>
                {tab === 'consumptions' &&
                    <RevField label={t('account.credit.event')} htmlFor="ds-cred-event" className="rev-field--w280">
                        <select
                            id="ds-cred-event"
                            className="rev-input"
                            value={eventId || ''}
                            onChange={(e) => setCreditFilters({ eventId: e.target.value || undefined })}>
                            <option value="">{t('account.consumptions.allEvents')}</option>
                            {eventosSeleccionables.map(event => (
                                <option key={event.id} value={event.id}>{event.name}</option>
                            ))}
                        </select>
                    </RevField>}
            </div>

            {tab === 'purchases' &&
                <RevCard
                    className={cx(refrescandoCompras && 'rev-refrescando')}
                    aria-busy={refrescandoCompras || undefined}
                    title={t('account.credit.purchasesTitle')}
                    head={<span className="rev-cred-saldo">
                        <span className="rev-help">{t('account.credit.currentBalance')}</span>
                        <RevFigure size="sm">{formatBalance(user?.balance, i18n.language)}</RevFigure>
                    </span>}
                    foot={<span className="rev-help">{t('account.credit.purchasesFoot')}</span>}>
                    {primeraCompras
                        ? <RevSkeleton.Cuerpo
                            tipo="tabla"
                            cabeceraTexto
                            filas={FILAS_ESQUELETO_CREDITOS}
                            celdas={['texto', 'texto', 'texto', 'texto', 'texto']}
                            clases={{
                                superficie: 'rev-cred-table',
                                cabecera: 'rev-table-head rev-cred-mov-head',
                                fila: 'rev-table-row rev-cred-mov-row',
                            }}
                            label={t('account.credit.purchasesTitle')} />
                        : movimientos.length === 0
                            ? <RevEmpty icon="receipt_long" title={t('account.credit.noUse')} />
                            : <div className="rev-cred-table">
                                <div className="rev-table-head rev-cred-mov-head">
                                    <span>{t('account.credit.date')}</span>
                                    <span>{t('account.credit.note')}</span>
                                    <span>{t('account.credit.type')}</span>
                                    <span className="rev-cred-cell--right">{t('account.credit.credit')}</span>
                                    <span className="rev-cred-cell--right">{t('account.credit.currentBalance')}</span>
                                </div>
                                {movimientos.map((fila, idx) => (
                                    <div
                                        className="rev-table-row rev-cred-mov-row"
                                        key={`${fila.operationDate}-${fila.type}-${idx}`}>
                                        <span className="rev-num">{fila.operationDate}</span>
                                        <span className="rev-table-row__ellipsis">{fila.concepto}</span>
                                        <span>{fila.tipo}</span>
                                        <span className="rev-num rev-cred-cell--right">{fila.cantidad}</span>
                                        <span className="rev-num rev-cred-cell--right">{fila.saldo}</span>
                                    </div>
                                ))}
                            </div>}
                </RevCard>}

            {tab === 'consumptions' && <>
                <div className={cx('rev-cred-metrics', refrescandoConsumos && 'rev-refrescando')}
                    aria-busy={refrescandoConsumos || undefined}>
                    <div className="rev-metric">
                        <span className="rev-metric__label">{t('account.credit.metrics.total')}</span>
                        {primeraConsumos
                            ? <RevFigure><RevSkeleton kind="cifra" /></RevFigure>
                            : <RevFigure>{fmt(consumo.total)}</RevFigure>}
                        <span className="rev-metric__delta">
                            {primeraConsumos
                                ? <RevSkeleton kind="texto" />
                                : t('account.credit.metrics.daysWithAccess', { count: consumo.conAcceso })}
                        </span>
                    </div>
                    <div className="rev-metric">
                        <span className="rev-metric__label">{t('account.credit.metrics.avg')}</span>
                        {primeraConsumos
                            ? <RevFigure><RevSkeleton kind="cifra" /></RevFigure>
                            : <RevFigure>{decimalFormatter.format(consumo.media)}</RevFigure>}
                    </div>
                    <div className="rev-metric">
                        <span className="rev-metric__label">{t('account.credit.metrics.peak')}</span>
                        {primeraConsumos
                            ? <RevFigure><RevSkeleton kind="cifra" /></RevFigure>
                            : !consumo.punta
                                ? <RevFigure muted>—</RevFigure>
                                : <RevFigure>{fmt(consumo.punta.quantity || 0)}</RevFigure>}
                        {!primeraConsumos && consumo.punta &&
                            <span className="rev-metric__delta rev-num">{consumo.punta.operationDate}</span>}
                    </div>
                    {!eventId &&
                        <div className="rev-metric">
                            <span className="rev-metric__label">{t('account.credit.metrics.runway')}</span>
                            {primeraConsumos
                                ? <RevFigure><RevSkeleton kind="cifra" /></RevFigure>
                                : !autonomia
                                    ? <RevFigure muted>—</RevFigure>
                                    : autonomia.dias > AUTONOMIA_MAX_DIAS
                                        ? <RevFigure>{t('account.credit.metrics.runwayYear')}</RevFigure>
                                        : <RevFigure>{t('account.credit.metrics.runwayDays', { count: autonomia.dias, n: fmt(autonomia.dias) })}</RevFigure>}
                            {!primeraConsumos && autonomia && autonomia.dias <= AUTONOMIA_MAX_DIAS &&
                                <span className="rev-metric__delta">
                                    {t('account.credit.metrics.runwayRate', { rate: decimalFormatter.format(autonomia.ritmo) })}
                                </span>}
                        </div>}
                </div>

                <RevCard
                    className={cx(refrescandoConsumos && 'rev-refrescando')}
                    aria-busy={refrescandoConsumos || undefined}
                    title={t(porMes ? 'account.credit.barsTitleMonth' : 'account.credit.barsTitle')}
                    sub={t('account.credit.barsSub')}>
                    {primeraConsumos
                        ? <RevSkeleton.Cuerpo
                            tipo="barras"
                            barras={BARRAS_ESQUELETO}
                            clases={ESQUELETO_BARRAS}
                            label={t('account.credit.barsTitle')} />
                        : barras.length === 0
                            ? <RevEmpty icon="groups" title={t('account.consumptions.noUse')} />
                            : <CreditsBars dias={barras} formatInteger={fmt} />}
                </RevCard>

                {detalleDias.length > 0 &&
                    <RevCard
                        className={cx(refrescandoConsumos && 'rev-refrescando')}
                        aria-busy={refrescandoConsumos || undefined}
                        title={t(porMes ? 'account.credit.detailTitleMonth' : 'account.credit.detailTitle')}>
                        <div className="rev-cred-table">
                            <div className={cx('rev-table-head', porMes ? 'rev-cred-month-head' : 'rev-cred-day-head')}>
                                <span>{t(porMes ? 'account.credit.month' : 'account.credit.day')}</span>
                                <span>{t('account.credit.event')}</span>
                                <span className="rev-cred-cell--right">{t('account.credit.newParticipants')}</span>
                                <span className="rev-cred-cell--right">{t('account.credit.closingBalance')}</span>
                            </div>
                            {porMes
                                ? filasDetalle(meses, c => nombreMes(c, { month: 'long', year: 'numeric' }), 'rev-cred-month-row')
                                : filasDetalle(detalleDias, c => c, 'rev-cred-day-row')}
                        </div>
                    </RevCard>}
            </>}
        </div>
        {redirect === 'buy' &&
            <Modal
                title={t('Purchase Successful!')}
                message={t('Your credit purchase has been successfully completed. You can now use them to enhance your experience. Thank you for your trust in us!')}
                showConfirmButton={true}
                confirmButtonText={t('View My Credits')}
                onCancel={() => { history.replace('/account/credit/home'); window.location.reload(); }}
                onConfirm={() => { history.replace('/account/credit/home'); window.location.reload(); }}
            />}
    </>);
};

export default withRouter(AccountCredit);
